/**
 * @file table的高度设置，使table的高度适应界面，使界面尽量在一屏显示，不出现滚动条
 * @author jiangjun
 * @usage 使用 Composition API 的 hook 形式（Vue 2.7）
 * 使用方法：
 *   const { tableHeight, calcHeight } = useSetTableHeight({
 *     height: 200, // 具体业务页面里所占用的高度
 *     pageDto: pageDto, // 分页对象（可选，用于监听分页变化）
 *     tableRef: tableRef, // table 的 ref（可选，用于重新计算布局）
 *     crmTableRef: crmTableRef // crmTable 的 ref（可选）
 *   })
 */

import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

// 一些固定高度
const fixedHeight = 60 + 16 * 2 + 24 * 2
// table的最小高度
const minHeight = 300

/**
 * 获取浏览器可视区域高度
 * @returns {Number} 浏览器可视区域高度
 */
const getClientHeight = () => {
    let clientHeight = 0
    if (document.body.clientHeight && document.documentElement.clientHeight) {
        clientHeight = (document.body.clientHeight < document.documentElement.clientHeight)
            ? document.body.clientHeight
            : document.documentElement.clientHeight
    } else {
        clientHeight = (document.body.clientHeight > document.documentElement.clientHeight)
            ? document.body.clientHeight
            : document.documentElement.clientHeight
    }
    return clientHeight
}

/**
 * 设置表格高度的 hook
 * @param {Object} options - 配置选项
 * @param {Number} options.height - 具体业务页面里所占用的高度
 * @param {Object} options.pageDto - 分页对象（可选）
 * @param {Object} options.tableRef - table 的 ref（可选）
 * @param {Object} options.crmTableRef - crmTable 的 ref（可选）
 * @returns {Object} 返回 tableHeight 和 calcHeight 方法
 */
export function useSetTableHeight(options = {}) {
    const {
        height = 0,
        pageDto = null,
        tableRef = null,
        crmTableRef = null
    } = options

    // 表格高度
    const tableHeight = ref(350)

    /**
     * 计算表格高度
     */
    const calcHeight = () => {
        setTimeout(() => {
            const clientHeight = getClientHeight()
            const calculatedHeight = (clientHeight - height) <= minHeight
                ? minHeight
                : clientHeight - (fixedHeight + height)

            tableHeight.value = calculatedHeight

            // 重新计算表格布局
            let table = null
            if (crmTableRef && crmTableRef.value) {
                table = crmTableRef.value.$children && crmTableRef.value.$children[0]
                    ? crmTableRef.value.$children[0]
                    : null
            } else if (tableRef && tableRef.value) {
                table = tableRef.value
            }

            if (table && typeof table.doLayout === 'function') {
                table.doLayout()
            }
        }, 0)
    }

    // 监听窗口大小变化
    const handleResize = () => {
        calcHeight()
    }

    // 组件挂载后计算高度
    onMounted(() => {
        nextTick(() => {
            calcHeight()
            window.addEventListener('resize', handleResize)
        })
    })

    // 监听分页变化
    if (pageDto) {
        watch(
            () => pageDto.totalPageNum,
            (newVal, oldVal) => {
                if (oldVal == 0 && newVal > 0) {
                    calcHeight()
                }
            }
        )
    }

    // 监听 height 变化（如果 height 是响应式的）
    if (typeof height === 'object' && height.value !== undefined) {
        watch(
            () => height.value,
            () => {
                calcHeight()
            }
        )
    }

    // 组件卸载前移除事件监听
    onBeforeUnmount(() => {
        window.removeEventListener('resize', handleResize)
    })

    return {
        tableHeight,
        calcHeight
    }
}